import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core'
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms'
import { Observable, Subscription } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { Criteria, GuestCount, GuestCriterion, GuestRestrictions } from '../../../../../graphql'
import { searchListingsCriteriaKey } from '../../../../constant'
import { UnsubscribeOnDestroy } from '../../../../utility'

const accessor: any = {
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SearchCriteriaGuestsComponent)
}
const validator: any = {
    multi: true,
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => SearchCriteriaGuestsComponent)
}

@Component({
    providers: [accessor, validator],
    selector: 'app-search-criteria-guests',
    templateUrl: 'search-criteria-guests.html'
})
export class SearchCriteriaGuestsComponent extends UnsubscribeOnDestroy implements ControlValueAccessor, Validator, OnInit, OnChanges, AfterContentInit {
    @Input() showTotalGuests: boolean
    @Input() activateChooseGuests = false
    @Input() inline = false
    @Input() border = true
    @Input() maxGuests = 100
    @Input() guestsRestrictions$: Observable<GuestRestrictions>

    defaultAgeGroups = ['adults', 'children', 'infants'] as const
    formGroup: FormGroup
    guestCriterion: GuestCriterion = {}
    guestsControl = new FormControl()
    chooseGuestsActive = false
    totalGuests = 1
    guestRestrictions: GuestRestrictions
    ageGroupsKeyValueMap = {}
    maxOccupancyReached = false
    maxOccupancyExceeded = false
    sub: Subscription

    onChange: (guestCriterion: GuestCriterion) => void
    onTouched: () => void

    @Output() totalGuestsChanged = new EventEmitter<number>()
    @Output() guestChanged = new EventEmitter<Array<GuestCount>>()
    @Output() maxGuestsExceeded = new EventEmitter<boolean>()

    @HostListener('document:keydown.escape', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        this.chooseGuestsActive = false
        this.activateChooseGuests = false
    }

    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        const targetElement = event.target as HTMLInputElement

        if (!this.eRef.nativeElement.contains(targetElement) && !this.activateChooseGuests) {
            this.chooseGuestsActive = false
        }

        this.activateChooseGuests = false
    }

    // Constructor
    constructor(private eRef: ElementRef<HTMLElement>) {
        super()
    }

    ngOnInit(): void {
        if (this.activateChooseGuests) {
            this.chooseGuestsActive = true
        }
    }

    ngAfterContentInit() {
        if (!this.inline) {
            return
        }
        const guestCriteria = this.initGuestCriteriaFromLocalStorage()
        const existingControls = Object.keys(this.formGroup.controls)
        const validGuestCriteria = Object.keys(guestCriteria)
            .filter(key => existingControls.includes(key))
            .reduce((acc, key) => ({ ...acc, [key]: guestCriteria[key] }), {})
        this.formGroup.patchValue(validGuestCriteria)
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.maxGuests?.previousValue !== changes?.maxGuests?.currentValue) {
            this.maxGuests = changes.maxGuests.currentValue
            this.updateSummary()
        }

        if (changes?.activateChooseGuests?.previousValue !== changes?.activateChooseGuests?.currentValue) {
            this.chooseGuestsActive = changes.activateChooseGuests.currentValue
        }
    }

    // Public
    registerOnChange(onChange) {
        this.onChange = onChange
    }

    registerOnTouched(onTouched) {
        this.onTouched = onTouched
    }

    validate(): ValidationErrors | null {
        return this.guestCriterion?.adults ? null : { guestCriterion: { valid: false } }
    }

    writeValue(guestCriterion: GuestCriterion | undefined) {
        if (guestCriterion) {
            this.guestCriterion = guestCriterion
        }

        this.guestsRestrictions$.subscribe((restrictions) => {
            this.guestRestrictions = restrictions
            this.initFormGroup(restrictions.ageRanges)
            this.updateSummary()
        })
    }

    clearFilters(fieldName: string) {
        this.formGroup?.get(fieldName)?.reset(fieldName !== 'adults' ? 0 : 1)
    }

    clearAll() {
        this.guestRestrictions.ageRanges.forEach(ageRange => {
            this.clearFilters(ageRange.key)
        })
    }

    private initFormGroup(ageGroups) {
        const group: any = {}

        ageGroups.forEach((ageGroup) => {
            this.ageGroupsKeyValueMap[ageGroup.key] = ageGroup.labelPrefix
            group[ageGroup.key] =
                this.defaultAgeGroups.indexOf(ageGroup.key) > -1  ?
                    new FormControl({ value: this.guestCriterion[ageGroup.key] || 0, disabled : !ageGroup.allowed }, Validators.required) :
                    new FormControl({ value: ageGroup.allowed ? ageGroup?.minNumberAllowed || 0 : 0, disabled: !ageGroup.allowed }, Validators.required)
        })

        this.formGroup = new FormGroup(group)

        this.guestCriterion = this.formGroup.value

        if (this.sub) {
            this.sub.unsubscribe()
        }

        this.sub = this.formGroup
            .valueChanges
            .pipe(
                debounceTime(500),
                takeUntil(this.destroy$)
            ).subscribe((guestCriterion) => {
                this.guestCriterion = guestCriterion

                this.updateSummary()

                this.onChange({
                    adults: guestCriterion?.adults || 1,
                    children: guestCriterion?.children || 0,
                    infants: guestCriterion?.infants || 0
                })
        })
    }

    private checkGuestRestrictionOccupancyLimits() {
        if (this.totalGuests === this.maxGuests) {
            this.setOccupancyLimits(true, false)

        } else if (this.totalGuests > this.maxGuests) {
            this.setOccupancyLimits(false, true)

        } else {
            this.setOccupancyLimits(false, false)
        }
    }

    private setOccupancyLimits(reached: boolean, exceeded: boolean) {
        this.maxOccupancyReached = reached
        this.maxOccupancyExceeded = exceeded
    }

    // Private
    private updateSummary() {
        if (this.guestCriterion) {
            this.totalGuests = 0

            Object.values(this.guestCriterion).forEach(ageRangeValue => {
                this.totalGuests = this.totalGuests + (ageRangeValue || 0)
            })

            this.checkGuestRestrictionOccupancyLimits()
            this.maxGuestsExceeded.emit(this.maxOccupancyExceeded)
            this.totalGuestsChanged.emit(this.totalGuests)
            this.guestsControl.setValue(this.totalGuests)
            this.guestChanged.next(Object.keys(this.guestCriterion).map(key => ({key, count: this.guestCriterion[key]}) as GuestCount))
        }
    }

    private initGuestCriteriaFromLocalStorage() {
        const criteriaRaw: string | null = sessionStorage.getItem(searchListingsCriteriaKey)

        if (criteriaRaw) {
            const criteria: Criteria = JSON.parse(criteriaRaw)
            if (criteria.guestCriterion) {
                return criteria.guestCriterion
            }
        }
        return {
            adults: 1,
            children: 0,
            infants: 0
        }
    }

}
